import { characters } from '../data/characters';
import { radicals } from '../data/radicals';
import { bigrams } from '../data/bigrams';
import { verbs } from '../data/verbs';
import { connectors } from '../data/conversationConnectors';
import { DECKS, getDeckBadge } from '../data/deckConfig';

// Data sources for each deck type
const DECK_DATA = {
  characters,
  radicals,
  bigrams,
  verbs,
  connectors,
};

// Get frequency level based on original index (for characters deck only)
export const getFrequencyLevel = (originalIndex) => {
  if (originalIndex < 100) return { level: 1, label: 'T1', color: '#22c55e', desc: '核心' };
  if (originalIndex < 200) return { level: 2, label: 'T2', color: '#84cc16', desc: '高频' };
  if (originalIndex < 300) return { level: 3, label: 'T3', color: '#eab308', desc: '常用' };
  if (originalIndex < 500) return { level: 4, label: 'T4', color: '#f97316', desc: '中频' };
  if (originalIndex < 700) return { level: 5, label: 'T5', color: '#ef4444', desc: '扩展' };
  return { level: 6, label: 'T6', color: '#a855f7', desc: '进阶' };
};

// Get badge info for any card (supports all deck types)
export const getCardBadge = (card) => {
  if (card.deckId === 'custom') {
    return { label: '我', color: '#FFD700', desc: '自定义' };
  }
  return getDeckBadge(card.deckId, card.originalIndex);
};

// Add original index and deck ID to each item for tracking
export const createIndexedData = (deckId) => {
  const data = DECK_DATA[deckId];
  if (!data) return [];
  return data.map((item, index) => ({
    ...item,
    originalIndex: index,
    deckId,
  }));
};

// Pre-indexed character data (for backwards compatibility)
export const charactersWithIndex = createIndexedData('characters');

// Simple shuffle utility
export const shuffleArray = (array) => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

// Remove consecutive duplicates and reinsert them elsewhere
const dedupeConsecutive = (deck) => {
  const result = [];
  let lastChar = null;
  const skipped = [];

  for (const card of deck) {
    if (card.char !== lastChar) {
      result.push(card);
      lastChar = card.char;
    } else {
      skipped.push(card);
    }
  }

  // Reinsert skipped cards at random non-consecutive positions
  for (const card of skipped) {
    let inserted = false;
    let attempts = 0;
    while (!inserted && attempts < 50) {
      const pos = Math.floor(Math.random() * (result.length + 1));
      const before = pos > 0 ? result[pos - 1]?.char : null;
      const after = result[pos]?.char;
      if (before !== card.char && after !== card.char) {
        result.splice(pos, 0, card);
        inserted = true;
      }
      attempts++;
    }
    if (!inserted) result.push(card);
  }

  return result;
};

// Create weighted deck for characters (T1 chars appear more often)
export const createWeightedDeck = (cards, isPro = false, customCards = []) => {
  const weighted = [];

  // Get deck config
  const config = DECKS.characters;

  // Filter for free users: only top 150 characters
  const availableCards = isPro ? cards : cards.filter(c => c.originalIndex < config.freeLimit);

  availableCards.forEach(card => {
    const idx = card.originalIndex;
    // Weight: T1 gets 6 copies, T2 gets 5, T3 gets 4, T4 gets 3, T5 gets 2, T6 gets 1
    let copies;
    if (idx < 100) copies = 6;
    else if (idx < 200) copies = 5;
    else if (idx < 300) copies = 4;
    else if (idx < 500) copies = 3;
    else if (idx < 700) copies = 2;
    else copies = 1;

    for (let i = 0; i < copies; i++) {
      weighted.push(card);
    }
  });

  // Blend custom cards if provided (3 copies each for visibility)
  if (customCards.length > 0) {
    customCards.forEach(card => {
      for (let i = 0; i < 3; i++) {
        weighted.push({
          ...card,
          deckId: 'custom',
          originalIndex: card.id || 0, // Use custom card ID as index
        });
      }
    });
  }

  // Shuffle and remove consecutive duplicates
  const shuffled = shuffleArray(weighted);
  return dedupeConsecutive(shuffled);
};

// Create equal distribution deck for non-weighted decks
export const createEqualDeck = (deckId, isPro = false) => {
  const config = DECKS[deckId];
  if (!config) return [];

  const data = createIndexedData(deckId);
  const copies = config.copies || 2;

  // Calculate available items based on subscription
  let availableData;
  if (config.freeLimit !== undefined) {
    availableData = isPro ? data : data.slice(0, config.freeLimit);
  } else if (config.freePercent !== undefined) {
    const limit = Math.floor(data.length * config.freePercent);
    availableData = isPro ? data : data.slice(0, limit);
  } else {
    availableData = data;
  }

  // Create copies
  const deck = [];
  availableData.forEach(card => {
    for (let i = 0; i < copies; i++) {
      deck.push(card);
    }
  });

  // Shuffle and remove consecutive duplicates
  const shuffled = shuffleArray(deck);
  return dedupeConsecutive(shuffled);
};

// Main function to create any deck type
export const createDeck = (deckId, isPro = false, customCards = []) => {
  const config = DECKS[deckId];
  if (!config) return [];

  if (deckId === 'characters') {
    // Use weighted deck for characters, optionally blending custom cards
    return createWeightedDeck(charactersWithIndex, isPro, customCards);
  }

  if (deckId === 'custom') {
    // Custom-only deck (just the user's custom cards)
    if (customCards.length === 0) return [];
    const copies = config.copies || 3;
    const deck = [];
    customCards.forEach((card, index) => {
      for (let i = 0; i < copies; i++) {
        deck.push({
          ...card,
          deckId: 'custom',
          originalIndex: card.id || index,
        });
      }
    });
    return dedupeConsecutive(shuffleArray(deck));
  }

  // All other decks use equal distribution
  return createEqualDeck(deckId, isPro);
};

// Get deck statistics
export const getDeckStats = (deckId, isPro = false) => {
  const config = DECKS[deckId];
  if (!config) return { total: 0, available: 0 };

  if (deckId === 'custom') {
    return { total: 0, available: 0 }; // Custom decks have variable size
  }

  const data = DECK_DATA[deckId] || [];
  const total = data.length;

  let available;
  if (config.freeLimit !== undefined) {
    available = isPro ? total : Math.min(total, config.freeLimit);
  } else if (config.freePercent !== undefined) {
    available = isPro ? total : Math.floor(total * config.freePercent);
  } else {
    available = total;
  }

  return { total, available };
};

// Get all available deck IDs for a user
export const getAvailableDeckIds = (isAuthenticated) => {
  return Object.keys(DECKS).filter(deckId => {
    const config = DECKS[deckId];
    if (config.requiresAuth && !isAuthenticated) return false;
    return true;
  });
};

// Check if a character already exists in a deck
export const isDuplicateCard = (char, deckId = 'characters') => {
  const data = DECK_DATA[deckId];
  if (!data) return false;
  return data.some(item => item.char === char);
};
